/* ------------------------------------------------------------------
   FiltersPanel
   • Desktop (vertical strip, 130 px)
   • Mobile  (horizontal scroll list + slider + apply btn)
------------------------------------------------------------------- */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Form } from 'react-bootstrap';
import useIsMobile from '../../../../../hooks/useIsMobile';

/* list of looks */
export const FILTERS = [
  { id: 'normal',    label: 'Normal',    css: '' },
  { id: 'clarendon', label: 'Clarendon', css: 'contrast(120%) saturate(125%)' },
  { id: 'gingham',   label: 'Gingham',   css: 'contrast(105%) brightness(105%) hue-rotate(350deg)' },
  { id: 'lofi',      label: 'Lofi',      css: 'contrast(135%) saturate(110%)' },
  { id: 'moon',      label: 'Moon',      css: 'grayscale(100%) contrast(110%) brightness(110%)' },
  { id: 'valencia',  label: 'Valencia',  css: 'contrast(105%) sepia(15%)' },
  { id: 'xpro2',     label: 'X-Pro II',  css: 'contrast(130%) saturate(120%) hue-rotate(5deg)' },
  { id: 'willow',    label: 'Willow',    css: 'grayscale(100%) brightness(120%) contrast(110%)' },
];

export default function FiltersPanel ({
  currentId    = 'normal',
  currentLevel = 100,
  onSelect     = () => {},
  onIntensity  = () => {},
  onApply      = () => {},
  dirty        = false,
}) {
  const isMobile = useIsMobile();
  const [hoverId, setHoverId] = useState(null);

  /* arrow-key shortcuts (desktop only) */
  useEffect(() => {
    if (isMobile) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const delta = e.key === 'ArrowUp' ? 5 : -5;
        onIntensity(Math.min(100, Math.max(0, currentLevel + delta)));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentLevel, onIntensity, isMobile]);

  /* ----  DESKTOP  -------------------------------------------------- */
  if (!isMobile) {
    return (
      <div
        className="position-absolute d-flex flex-column"
        style={{
          left: 0, top: 0, bottom: 0, width: 130,
          background: '#fff', overflowY: 'auto', zIndex: 2,
        }}
      >
        {FILTERS.map(f => (
          <Button
            key={f.id}
            variant={currentId === f.id ? 'primary' : 'light'}
            className="w-100 rounded-0 text-center py-2 small"
            onMouseEnter={() => setHoverId(f.id)}
            onMouseLeave={() => setHoverId(null)}
            onClick={() => onSelect(f.id)}
            style={{ fontSize: 13 }}
          >
            {(hoverId || currentId) === f.id ? '✓ ' : ''}
            {f.label}
          </Button>
        ))}

        <div className="px-2 py-3 border-top small">
          <Form.Label className="mb-1">Intensity</Form.Label>
          <Form.Range
            min={0} max={100}
            value={currentLevel}
            onChange={e => onIntensity(+e.target.value)}
          />
        </div>

        {dirty && (
          <Button
            variant="primary"
            className="mx-2 mb-3"
            size="sm"
            onClick={onApply}
          >
            Apply Filter
          </Button>
        )}
      </div>
    );
  }

  /* ----  MOBILE  --------------------------------------------------- */
  return (
    <div className="p-2 d-flex flex-column" style={{ width: '100%' }}>
      {/* horizontal-scroll list */}
      <div
        className="d-flex mb-3"
        style={{
          overflowX: 'auto',
          overscrollBehaviorX: 'contain',
          scrollSnapType: 'x mandatory',
        }}
      >
        {FILTERS.map(f => (
          <Button
            key={f.id}
            variant={currentId === f.id ? 'primary' : 'light'}
            className="me-2 text-nowrap"
            style={{
              flex: '0 0 90px',
              scrollSnapAlign: 'center',
              fontSize: 13,
            }}
            onClick={() => onSelect(f.id)}
          >
            {currentId === f.id ? '✓ ' : ''}
            {f.label}
          </Button>
        ))}
      </div>

      {/* intensity */}
      <div className="px-1 mb-3 small">
        <Form.Label className="mb-1">Intensity</Form.Label>
        <Form.Range
          min={0} max={100}
          value={currentLevel}
          onChange={e => onIntensity(+e.target.value)}
        />
      </div>

      {/* apply */}
      {dirty && (
        <Button variant="primary" size="sm" onClick={onApply}>
          Apply Filter
        </Button>
      )}
    </div>
  );
}

FiltersPanel.propTypes = {
  currentId   : PropTypes.string,
  currentLevel: PropTypes.number,
  onSelect    : PropTypes.func,
  onIntensity : PropTypes.func,
  onApply     : PropTypes.func,
  dirty       : PropTypes.bool,
};
